<template>
  <div class="assist-request-content">
    <!-- チャットメッセージ -->
    <ChatMessage
      message="何かできることはあるかにゃ？"
      :cat-icon-size="'medium'"
      :use-writer-type="true"
      class="assist-request-content__message"
    />

    <!-- アシスタント情報バナー -->
    <div v-if="shouldShowInfoBanner" class="assist-request-content__info-banner">
      <div class="info-banner-content">
        <p class="info-banner-text">
          AIを活用したアシスタント猫があなたの執筆をサポートします。<br />
          まだ見習い中のため、的を射ていないお返事や、読むのが難しい内容があるかもしれません。<br />
          特に、とても刺激的なシーンや少し暴力的なシーンは苦手です。<br />
          ご了承いただけますと幸いです。
        </p>
      </div>
      <button class="info-banner-close" type="button" @click="handleInfoBannerClose">
        <span class="close-icon">×</span>
      </button>
    </div>

    <!-- 依頼ボタン群 -->
    <ActionButtonGroup
      :buttons="requestButtons"
      class="assist-request-content__suggestions"
      @button-click="handleRequestClick"
      @icon-click="handleIconClick"
    />

    <!-- 注意文言 -->
    <div class="assist-request-content__notice">
      <p v-if="isThemeContext && isThemeMatchingDisabled && !isThemeMatchingHidden" class="notice-text">
        {{ $t("themeMatching.validation.fanFictionDisabled") }}
      </p>
      <p v-if="isThemeContext" class="notice-text">
        {{ $t("themeMatching.confirm.noticeNoLearning") }}
      </p>
      <p v-else class="notice-text">※ 本機能のご利用にあたり、原稿データがAIの学習に利用されることはございません。</p>
    </div>
    <FeedbackManuscriptModeSelectionDialog
      v-if="showFeedbackManuscriptModeDialog"
      :assistant-cat-master="assistantCatMaster"
      @close="onCloseFeedbackManuscriptModeDialog"
    />
    <GenerateSummaryModeSelectionDialog
      v-if="showGenerateSummaryModeDialog"
      :assistant-cat-master="assistantCatMaster"
      @close="onCloseGenerateSummaryModeDialog"
    />
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { mapGetters } from "vuex";
import ChatMessage from "@/components/molecules/ChatMessage.vue";
import ActionButtonGroup, { ActionButtonData } from "@/components/molecules/ActionButtonGroup.vue";
import { logWarn, logError } from "@/lib/utils/logger";
import { getAssistMessage } from "@/constants/assistMessages";
import { FeedbackManuscriptMode, GenerateSummaryMode, AssistPanelContext } from "@/lib/models/assist";
import {
  useThemeMatchingValidation,
  isFanFictionNovel,
  isScriptModeNovel,
  NovelLike,
} from "@/composables/useThemeMatchingValidation";
import type { AssistantCatMaster } from "@/lib/models/assistantCat";
import normalModeIcon from "@/assets/img/icon/mode/normal.svg";
import dryModeIcon from "@/assets/img/icon/mode/dry.svg";
import sweetModeIcon from "@/assets/img/icon/mode/sweet.svg";
import wakuwakuModeIcon from "@/assets/img/icon/mode/wakuwaku.svg";
import fumufumuModeIcon from "@/assets/img/icon/mode/fumufumu.svg";
import FeedbackManuscriptModeSelectionDialog from "@/components/ui/FeedbackManuscriptModeSelectionDialog.vue";
import GenerateSummaryModeSelectionDialog from "@/components/ui/GenerateSummaryModeSelectionDialog.vue";
import { useThemeReviewValidation } from "@/composables/useThemeReviewValidation";
import { createThemeGenerateRedirectLocation } from "@/lib/utils/themeGenerateRedirect";

// 🆕 より具体的で型安全なインターフェース
interface RequestSuggestionMeta {
  id: "feedback" | "matching" | "catchcopy" | "summary" | "theme-generate" | "theme-feedback" | "theme-matching";
  action:
    | "request-feedback"
    | "request-matching"
    | "request-catchcopy"
    | "request-summary"
    | "request-theme-generate"
    | "request-theme-feedback"
    | "request-theme-matching";
}

type RequestSuggestion = RequestSuggestionMeta & { text: string };

interface AssistRequestContentData {
  showFeedbackManuscriptModeDialog: boolean;
  showGenerateSummaryModeDialog: boolean;
  themeValidationProcessing: boolean;
}

interface AssistRequestContentProps {
  context: AssistPanelContext;
}

interface AssistRequestContentMethods {
  handleRequestClick(buttonText: string): void;
  executeFeedbackAction(): void;
  executeMatchingAction(): void;
  executeCatchcopyAction(): void;
  executeSummaryAction(): void;
  executeThemeGenerateAction(): void;
  executeThemeGenerateRedirect(): void;
  executeThemeFeedbackAction(): Promise<void>;
  executeThemeMatchingAction(): Promise<void>;
  handleInfoBannerClose(): void;
  handleIconClick(id?: string): void;
  onCloseFeedbackManuscriptModeDialog(): void;
  onCloseGenerateSummaryModeDialog(): void;
  ensureNovelLoaded(): Promise<void>;
}

interface AssistRequestContentComputed {
  isThemeContext: boolean;
  currentNovel: NovelLike | undefined;
  isThemeMatchingDisabled: boolean;
  themeMatchingDisabledReason: string;
  isThemeMatchingHidden: boolean;
  feedbackSuggestion: RequestSuggestion;
  matchingSuggestion: RequestSuggestion;
  catchcopySuggestion: RequestSuggestion;
  summarySuggestion: RequestSuggestion;
  themeGenerateSuggestion: RequestSuggestion;
  themeFeedbackSuggestion: RequestSuggestion;
  themeMatchingSuggestion: RequestSuggestion;
  requestButtons: ActionButtonData[];
  shouldShowInfoBanner: boolean;
  assistantCatMaster: AssistantCatMaster | null;
  currentFeedbackModeIcon: string;
  currentSummaryModeIcon: string;
}

export default Vue.extend<
  AssistRequestContentData,
  AssistRequestContentMethods,
  AssistRequestContentComputed,
  AssistRequestContentProps
>({
  name: "AssistRequestContent",
  components: {
    ChatMessage,
    ActionButtonGroup,
    FeedbackManuscriptModeSelectionDialog,
    GenerateSummaryModeSelectionDialog,
  },
  props: {
    context: {
      type: String as () => AssistPanelContext,
      default: "manuscript",
    },
  },
  data(): AssistRequestContentData {
    return {
      showFeedbackManuscriptModeDialog: false,
      showGenerateSummaryModeDialog: false,
      themeValidationProcessing: false,
    };
  },
  computed: {
    isThemeContext(): boolean {
      return this.context === "theme";
    },

    currentNovel(): NovelLike | undefined {
      const novelId = this.$route.params.novelId as string | undefined;
      if (!novelId) return undefined;
      return this.$store.getters["novelModule/novel"](novelId);
    },

    isThemeMatchingDisabled(): boolean {
      return this.isThemeContext && isFanFictionNovel(this.currentNovel);
    },

    themeMatchingDisabledReason(): string {
      if (!this.isThemeMatchingDisabled) return "";
      return this.$t("themeMatching.validation.fanFictionDisabled") as string;
    },

    // 脚本モードの作品では企画マッチングボタン自体を表示しない
    isThemeMatchingHidden(): boolean {
      return this.isThemeContext && isScriptModeNovel(this.currentNovel);
    },

    feedbackSuggestion(): RequestSuggestion {
      return {
        id: "feedback",
        text: getAssistMessage("FEEDBACK_MANUSCRIPT", "requestTitle"),
        action: "request-feedback",
      };
    },
    matchingSuggestion(): RequestSuggestion {
      return {
        id: "matching",
        text: getAssistMessage("MATCHING_COMPANY", "requestTitle"),
        action: "request-matching",
      };
    },
    catchcopySuggestion(): RequestSuggestion {
      return {
        id: "catchcopy",
        text: getAssistMessage("GENERATE_CATCHCOPY", "requestTitle"),
        action: "request-catchcopy",
      };
    },
    summarySuggestion(): RequestSuggestion {
      return {
        id: "summary",
        text: getAssistMessage("GENERATE_SUMMARY", "requestTitle"),
        action: "request-summary",
      };
    },
    themeGenerateSuggestion(): RequestSuggestion {
      return {
        id: "theme-generate",
        text: getAssistMessage("GENERATE_THEME", "requestTitle"),
        action: "request-theme-generate",
      };
    },
    themeFeedbackSuggestion(): RequestSuggestion {
      return {
        id: "theme-feedback",
        text: getAssistMessage("FEEDBACK_THEME", "requestTitle"),
        action: "request-theme-feedback",
      };
    },
    themeMatchingSuggestion(): RequestSuggestion {
      return {
        id: "theme-matching",
        text: getAssistMessage("MATCHING_THEME", "requestTitle"),
        action: "request-theme-matching",
      };
    },

    // 依頼ボタン群の生成
    requestButtons(): ActionButtonData[] {
      // theme ページでは企画レビュー → 企画マッチング → 企画生成の順で表示
      if (this.isThemeContext) {
        const buttons: ActionButtonData[] = [
          {
            id: this.themeFeedbackSuggestion.id,
            text: this.themeFeedbackSuggestion.text,
          },
        ];

        if (!this.isThemeMatchingHidden) {
          buttons.push({
            id: this.themeMatchingSuggestion.id,
            text: this.themeMatchingSuggestion.text,
            disabled: this.isThemeMatchingDisabled,
            disabledReason: this.themeMatchingDisabledReason || undefined,
          });
        }

        buttons.push({
          id: this.themeGenerateSuggestion.id,
          text: this.themeGenerateSuggestion.text,
        });

        return buttons;
      }

      return [
        {
          id: this.feedbackSuggestion.id,
          text: this.feedbackSuggestion.text,
          iconSrc: this.currentFeedbackModeIcon,
          iconClickable: true,
        },
        {
          id: this.matchingSuggestion.id,
          text: this.matchingSuggestion.text,
        },
        {
          id: this.catchcopySuggestion.id,
          text: this.catchcopySuggestion.text,
        },
        {
          id: this.summarySuggestion.id,
          text: this.summarySuggestion.text,
          iconSrc: this.currentSummaryModeIcon,
          iconClickable: true,
        },
        {
          id: this.themeGenerateSuggestion.id,
          text: this.themeGenerateSuggestion.text,
        },
      ];
    },

    currentFeedbackModeIcon(): string {
      const feedbackManuscriptMode = this.assistantCatMaster?.feedbackManuscriptMode ?? FeedbackManuscriptMode.NORMAL;
      if (feedbackManuscriptMode === FeedbackManuscriptMode.DRY) return dryModeIcon;
      if (feedbackManuscriptMode === FeedbackManuscriptMode.SWEET) return sweetModeIcon;
      return normalModeIcon;
    },

    currentSummaryModeIcon(): string {
      const generateSummaryMode = this.assistantCatMaster?.generateSummaryMode ?? GenerateSummaryMode.WAKUWAKU;
      return generateSummaryMode === GenerateSummaryMode.FUMUFUMU ? fumufumuModeIcon : wakuwakuModeIcon;
    },

    // Vuex gettersをmapしてバナー表示状態を取得
    ...mapGetters("assistNavigationModule", {
      shouldShowInfoBanner: "shouldShowInfoBanner",
    }),
    ...mapGetters("assistantCatModule", {
      assistantCatMaster: "assistantCatMaster",
    }),
  },
  created() {
    if (this.isThemeContext) {
      this.ensureNovelLoaded();
    }
  },
  watch: {
    $route() {
      if (this.isThemeContext) {
        this.ensureNovelLoaded();
      }
    },
  },
  methods: {
    // 依頼ボタン押下処理
    handleRequestClick(buttonText: string): void {
      if (
        this.isThemeContext &&
        buttonText === this.themeMatchingSuggestion.text &&
        (this.isThemeMatchingDisabled || this.isThemeMatchingHidden)
      ) {
        return;
      }
      // 押下されたボタンに応じて親コンポーネントにイベントを送信
      if (this.isThemeContext && buttonText === this.themeGenerateSuggestion.text) {
        this.executeThemeGenerateAction();
        this.$emit("suggestion-click", this.themeGenerateSuggestion);
      } else if (this.isThemeContext && buttonText === this.themeFeedbackSuggestion.text) {
        this.executeThemeFeedbackAction();
      } else if (this.isThemeContext && buttonText === this.themeMatchingSuggestion.text) {
        this.executeThemeMatchingAction();
      } else if (buttonText === this.feedbackSuggestion.text) {
        this.executeFeedbackAction();
        this.$emit("suggestion-click", this.feedbackSuggestion);
      } else if (buttonText === this.matchingSuggestion.text) {
        this.executeMatchingAction();
        this.$emit("suggestion-click", this.matchingSuggestion);
      } else if (buttonText === this.catchcopySuggestion.text) {
        this.executeCatchcopyAction();
        this.$emit("suggestion-click", this.catchcopySuggestion);
      } else if (buttonText === this.summarySuggestion.text) {
        this.executeSummaryAction();
        this.$emit("suggestion-click", this.summarySuggestion);
      } else if (buttonText === this.themeGenerateSuggestion.text) {
        this.executeThemeGenerateRedirect();
      } else {
        logWarn({ module: "AssistRequestContent", action: "handleRequestClick" }, "予期しないボタンテキスト", {
          buttonText,
          expected: [
            this.feedbackSuggestion.text,
            this.matchingSuggestion.text,
            this.catchcopySuggestion.text,
            this.summarySuggestion.text,
            this.themeGenerateSuggestion.text,
            this.themeFeedbackSuggestion.text,
            this.themeMatchingSuggestion.text,
          ],
        });
      }
    },

    handleIconClick(id?: string): void {
      if (id === "feedback") {
        this.showFeedbackManuscriptModeDialog = true;
      } else if (id === "summary") {
        this.showGenerateSummaryModeDialog = true;
      }
    },

    // 感想依頼処理
    executeFeedbackAction(): void {
      try {
        // 感想依頼 → 原稿選択画面に遷移
        this.$store.dispatch("assistNavigationModule/showManuscriptSelect");
      } catch (error) {
        logError(
          { module: "AssistRequestContent", action: "executeFeedbackAction" },
          "感想依頼アクション実行エラー",
          error
        );
      }
    },

    // 応募先検索処理
    executeMatchingAction(): void {
      try {
        // 応募先検索 → 原稿選択画面に遷移
        this.$store.dispatch("assistNavigationModule/showManuscriptSelect");
      } catch (error) {
        logError(
          { module: "AssistRequestContent", action: "executeMatchingAction" },
          "応募先検索アクション実行エラー",
          error
        );
      }
    },

    // キャッチコピー生成処理
    executeCatchcopyAction(): void {
      try {
        // キャッチコピー生成 → 原稿選択画面に遷移
        this.$store.dispatch("assistNavigationModule/showManuscriptSelect");
      } catch (error) {
        logError(
          { module: "AssistRequestContent", action: "executeCatchcopyAction" },
          "キャッチコピー生成アクション実行エラー",
          error
        );
      }
    },

    // あらすじ生成処理
    executeSummaryAction(): void {
      try {
        // あらすじ生成 → 原稿選択画面に遷移
        this.$store.dispatch("assistNavigationModule/showManuscriptSelect");
      } catch (error) {
        logError(
          { module: "AssistRequestContent", action: "executeSummaryAction" },
          "あらすじ生成アクション実行エラー",
          error
        );
      }
    },

    // 企画項目生成（キャッチコピーと同じく原稿選択へ。バリデーションなし）
    executeThemeGenerateAction(): void {
      try {
        this.$store.dispatch("assistNavigationModule/showManuscriptSelect");
      } catch (error) {
        logError(
          { module: "AssistRequestContent", action: "executeThemeGenerateAction" },
          "企画項目生成アクション実行エラー",
          error
        );
      }
    },

    // 原稿画面から企画ページへ移動し、企画項目生成の原稿選択画面を開く
    executeThemeGenerateRedirect(): void {
      const novelId = this.$route.params.novelId as string | undefined;
      if (!novelId) {
        logWarn(
          { module: "AssistRequestContent", action: "executeThemeGenerateRedirect" },
          "novelId が取得できませんでした"
        );
        return;
      }

      this.$router.push(createThemeGenerateRedirectLocation(novelId)).catch((error) => {
        logError(
          { module: "AssistRequestContent", action: "executeThemeGenerateRedirect" },
          "企画ページへの遷移に失敗しました",
          error
        );
      });
    },

    // 企画レビュー依頼処理（2 段階バリデーション → 確認画面へ遷移）
    async executeThemeFeedbackAction(): Promise<void> {
      if (this.themeValidationProcessing) return;
      this.themeValidationProcessing = true;

      try {
        const novelId = this.$route.params.novelId as string | undefined;
        if (!novelId) {
          logWarn(
            { module: "AssistRequestContent", action: "executeThemeFeedbackAction" },
            "novelId が取得できませんでした"
          );
          await this.$store.dispatch(
            "assistNavigationModule/setError",
            this.$t("themeReview.validation.prepareErrorMessage")
          );
          return;
        }

        // 作品データ（ジャンル・カテゴリ判定用）を取得
        let novel = this.$store.getters["novelModule/novel"](novelId);
        if (!novel) {
          novel = await this.$store.dispatch("novelModule/fetchNovel", { novelId });
        }

        const { validate } = useThemeReviewValidation();
        const valid = await validate({ novelId, novel });
        if (!valid) return;

        // 遷移はここで完結する。親の suggestion-click は theme では何もしないため emit しない
        await this.$store.dispatch("assistNavigationModule/showThemeReviewConfirm");
      } catch (error) {
        logError(
          { module: "AssistRequestContent", action: "executeThemeFeedbackAction" },
          "企画レビュー依頼アクション実行エラー",
          error
        );
        // fetchNovel 失敗等で無反応にならないよう、パネル内にエラーを表示する
        await this.$store.dispatch(
          "assistNavigationModule/setError",
          this.$t("themeReview.validation.prepareErrorMessage")
        );
      } finally {
        this.themeValidationProcessing = false;
      }
    },

    async executeThemeMatchingAction(): Promise<void> {
      if (this.themeValidationProcessing) return;
      if (this.isThemeMatchingDisabled || this.isThemeMatchingHidden) return;
      this.themeValidationProcessing = true;

      try {
        const novelId = this.$route.params.novelId as string | undefined;
        if (!novelId) {
          logWarn(
            { module: "AssistRequestContent", action: "executeThemeMatchingAction" },
            "novelId が取得できませんでした"
          );
          await this.$store.dispatch(
            "assistNavigationModule/setError",
            this.$t("themeMatching.validation.prepareErrorMessage")
          );
          return;
        }

        let novel = this.$store.getters["novelModule/novel"](novelId);
        if (!novel) {
          novel = await this.$store.dispatch("novelModule/fetchNovel", { novelId });
        }

        const { validate } = useThemeMatchingValidation();
        const valid = await validate({ novelId, novel });
        if (!valid) return;

        await this.$store.dispatch("assistNavigationModule/showThemeMatchingConfirm");
      } catch (error) {
        logError(
          { module: "AssistRequestContent", action: "executeThemeMatchingAction" },
          "企画マッチング依頼アクション実行エラー",
          error
        );
        await this.$store.dispatch(
          "assistNavigationModule/setError",
          this.$t("themeMatching.validation.prepareErrorMessage")
        );
      } finally {
        this.themeValidationProcessing = false;
      }
    },

    onCloseFeedbackManuscriptModeDialog(): void {
      this.showFeedbackManuscriptModeDialog = false;
    },

    onCloseGenerateSummaryModeDialog(): void {
      this.showGenerateSummaryModeDialog = false;
    },

    async ensureNovelLoaded(): Promise<void> {
      const novelId = this.$route.params.novelId as string | undefined;
      if (!novelId) return;
      if (this.$store.getters["novelModule/novel"](novelId)) return;
      try {
        await this.$store.dispatch("novelModule/fetchNovel", { novelId });
      } catch (error) {
        logWarn({ module: "AssistRequestContent", action: "ensureNovelLoaded" }, "作品情報の取得に失敗", error);
      }
    },

    // 🆕 情報バナーのクローズ処理
    handleInfoBannerClose(): void {
      try {
        this.$store.dispatch("assistNavigationModule/dismissInfoBanner");
      } catch (error) {
        logError(
          { module: "AssistRequestContent", action: "handleInfoBannerClose" },
          "情報バナーのクローズエラー",
          error
        );
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";
.assist-request-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-height: 100%;
}

.assist-request-content__info-banner {
  position: relative;
  background-color: var(--color-background-normal);
  border-radius: var(--radius-sm);
  padding: 12px 32px 12px 16px;
}

.info-banner-content {
  width: 100%;
}

.info-banner-text {
  margin: 0;
  color: var(--color-text-normal);
  font-size: var(--font-size-xs);
  line-height: 1.5;
  font-weight: var(--font-weight-regular);
}

.info-banner-close {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-rounded);
  transition: background-color 0.2s ease;

  @include hover-overlay;

  &:focus {
    outline: 2px solid var(--color-primary);
    outline-offset: 1px;
  }
}

.close-icon {
  font-size: var(--font-size-md);
  color: var(--color-text-low);
  font-weight: var(--font-weight-bold);
  line-height: 1;
}

.assist-request-content__message {
  margin-bottom: 4px;
}

.assist-request-content__notice {
  margin-top: auto;
  text-align: left;
}

.notice-text {
  margin: 0;
  color: var(--color-inactive);
  font-size: var(--font-size-xxs);
  line-height: 1.4;
  font-weight: var(--font-weight-regular);
}

/* レスポンシブ対応 */
@media (max-width: 576px) {
  .assist-request-content {
    gap: 16px;
  }

  .assist-request-content__info-banner {
    padding: 12px 36px 12px 12px;
  }

  .info-banner-text {
    font-size: var(--font-size-xs);
    line-height: 1.4;
  }

  .info-banner-close {
    top: 6px;
    right: 6px;
    width: 20px;
    height: 20px;
  }

  .close-icon {
    font-size: var(--font-size-sm);
  }

  .assist-request-content__notice {
    padding: 10px 0;
  }

  .notice-text {
    font-size: 9px;
    line-height: 1.3;
  }
}
</style>
